import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { TableHeader } from 'components/table/table-content';
import { Filters } from 'elements/Filter';
import { PubSub } from 'lib/event/PubSub';
import { FilterManager } from 'lib/tables/FilterManager';
import { AsyncErrorHandler } from 'lib/ui';
import { ErpSystem } from 'models/ErpSystem';
import { ErrorService } from 'services/error-service';
import { ForeignObjectTypeServiceService } from 'services/foreign-object-type-service-service';

type ForeignObjectType = {
  Id: number;
  Name: string;
  ArticleNo: string;
  ErpSystemName: string;
};

@autoinject
export class ForeignObjectTypeList {
  protected tableData: Array<ForeignObjectType>;
  protected activeTableRow: number;

  protected erpSystems: ErpSystem[] = [];

  protected tableHeaders: TableHeader<ForeignObjectType>[] = [
    { key: 'Name', label: 'general.name', sortable: true },
    { key: 'ArticleNo', label: 'general.articleno', sortable: true },
    { key: 'ErpSystemName', label: 'general.erpSystem', sortable: false },
  ];

  private constructor(
    private errorService: ErrorService,
    private eventAggregator: EventAggregator,
    private router: Router,
    private foreignObjectTypeServiceService: ForeignObjectTypeServiceService,
    private t: I18N,
    protected fm: FilterManager,
    private pubsub: PubSub
  ) {
    pubsub.sub('filter-manager:filter-changed', async () => {
      await this.updateView();
    });
  }

  protected activate(params: { Id: number }) {
    if (params.Id) {
      this.activeTableRow = params.Id;
    } else {
      this.activeTableRow = 0;
    }
  }

  protected async attached() {
    this.eventAggregator.subscribe('foreignObjectTypeListReset', () => {
      this.activeTableRow = null;
      return this.updateView();
    });

    this.fm.init({
      context: 'foreign-object-type',
      storageKey: 'foreign-object-type',
      filters: {} satisfies Filters,
      defaultFilters: {
        erpIds: true,
      },
    });

    await this.updateView();
  }

  @AsyncErrorHandler
  async updateView() {
    try {
      const response = await this.foreignObjectTypeServiceService.getForeignObjectTypeServiceList(this.fm.queryFilter);

      if (!response) return;
      this.fm.paginationTotalRecords = +response.headers.get('x-total-count') || 0;

      this.tableData = response.data;
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  protected setOrderByColumn(column: string) {
    this.fm.setOrderByColumn(column);
  }

  protected gotoDetailView(id: number) {
    this.activeTableRow = id;
    this.router.navigateToRoute('foreign-object-type-detail', { Id: id });
  }

  protected detached() {
    this.pubsub.unsub();
  }

  protected unbind() {
    this.pubsub.unsub();
  }
}
